import { useState } from "react";
import { useNavigate } from "react-router";
import { brand } from "../brand.js";

/** Full-state prompt shown to first-visit users who haven't configured their
 * provider yet. On Home, render as a dismissible banner above the hero; on
 * Live/Movies/Series, render as the full error state so the user isn't stuck
 * with a dead page. Dismissed state is in-memory only — refreshes on reload
 * so the prompt reappears if the provider is still unconfigured. */

export function ProviderSetupPrompt({ fullPage = false }: { fullPage?: boolean }) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  if (fullPage) {
    return (
      <div className="provider-prompt provider-prompt--full">
        <div className="provider-prompt__card">
          <div className="provider-prompt__logo">{brand().appName}</div>
          <h2 className="provider-prompt__title">Welcome to {brand().appName}</h2>
          <p className="provider-prompt__body">
            Connect your source (Xtream code or M3U URL) to start watching
            live TV, movies, and series.
          </p>
          <div className="provider-prompt__actions">
            <button type="button" className="ui-btn ui-btn--primary" onClick={() => navigate("/settings/source")}>
              Set Up Source
            </button>
            <button type="button" className="ui-btn ui-btn--ghost" onClick={() => setDismissed(true)}>
              Explore without a provider
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="provider-prompt provider-prompt--banner">
      <div className="provider-prompt__banner">
        <span className="provider-prompt__banner-text">
          <strong>Source not connected.</strong> Set up your Xtream code or M3U URL to see your channels, movies, and guide.
        </span>
        <button type="button" className="ui-btn ui-btn--primary provider-prompt__banner-btn" onClick={() => navigate("/settings/source")}>
          Set Up Source
        </button>
        <button type="button" className="provider-prompt__banner-dismiss" onClick={() => setDismissed(true)} aria-label="Dismiss">
          ×
        </button>
      </div>
    </section>
  );
}

/** Returns true when the error from a channels/VOD query means "no provider
 * configured" so callers can swap the generic error UI for the setup prompt. */
export function isProviderNotConfigured(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { code?: unknown; message?: unknown };
  return e.code === "provider_not_configured" || (typeof e.message === "string" && e.message.includes("provider not configured"));
}
